import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { AppRoutes } from '@/constants/appRoutes'
import {
  useCreatePortalSessionMutation,
  useCreateCheckoutSessionMutation,
  useGetPublicPricingTableQuery,
  useGetTopupProductsQuery,
} from '@/store/api/Stripe/Stripe.query'
import type {
  CheckoutSessionResponse,
  PortalSessionResponse,
  PricingProduct,
} from '@/store/api/Stripe/Stripe.types'

interface UseStripeOptions {
  skipTopup?: boolean
  /** Product family for the pricing table and checkout metadata (e.g. 'impersonation') */
  productType?: string
  /** App route to return to after a successful checkout (defaults to the generic payment success) */
  successRoute?: string
}

export const useStripe = (options?: UseStripeOptions) => {
  const { skipTopup = false, productType, successRoute } = options || {}

  const [createPortalSession] = useCreatePortalSessionMutation()
  const [createCheckoutSession] = useCreateCheckoutSessionMutation()

  const [userIp, setUserIp] = useState<string | undefined>(undefined)
  const [ipFetched, setIpFetched] = useState(false)

  useEffect(() => {
    const fetchIp = async () => {
      try {
        // Set a timeout for the IP fetch to avoid hanging the UI
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        const response = await fetch('https://api.ipify.org?format=json', {
          signal: controller.signal,
        })
        const data = await response.json()
        setUserIp(data.ip)
        clearTimeout(timeoutId)
      } catch (error) {
        console.error('Failed to fetch IP:', error)
      } finally {
        setIpFetched(true)
      }
    }

    fetchIp()
  }, [])

  /**
   * Retrive Pricing Table Data
   */
  const {
    data: pricingData,
    isLoading: isLoadingPricing,
    isSuccess: isPricingSuccess,
  } = useGetPublicPricingTableQuery(
    userIp || productType ? { ip: userIp, productType } : undefined,
    {
      skip: !ipFetched,
    },
  )

  /**
   * Retrive Topup Products Data
   */
  const { data: topupProductData, isLoading: isLoadingTopupProductsData } =
    useGetTopupProductsQuery(userIp ? { ip: userIp } : undefined, {
      skip: !ipFetched || skipTopup,
    })

  /**
   * Handle plan selection and redirect to checkout
   */
  const handleSelectPlan = async (
    product: PricingProduct,
    userContext?: { email?: string; customerId?: string },
  ) => {
    try {
      const mode = product.isRecurring ? 'subscription' : 'payment'

      const successUrl = userContext?.customerId
        ? `${window.location.origin}${
            successRoute || AppRoutes.paymentSuccess
          }?session_id={CHECKOUT_SESSION_ID}`
        : `${window.location.origin}${AppRoutes.stripePaymentRedirect}?session_id={CHECKOUT_SESSION_ID}`

      const response = await createCheckoutSession({
        mode,
        lineItems: [
          {
            price: product.priceId,
            quantity: 1,
          },
        ],
        successUrl,
        cancelUrl: window.location.href,
        customerEmail: userContext?.email,
        customerId: userContext?.customerId,
        allowPromotionCodes: true,
        metadata: {
          planType: product.type,
          credits: product.credits.toString(),
          ...(productType === 'impersonation'
            ? { creditType: 'impersonation' }
            : {}),
        },
      }).unwrap()

      if (response.url) {
        window.location.href = response.url
      } else {
        throw new Error('No checkout URL received')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      toast.error('Could not initiate checkout. Please try again.')
    }
  }

  /**
   * Generate a portal session for the user
   */
  const generatePortalSession = async (
    stripeCustomerId: string,
  ): Promise<PortalSessionResponse | null> => {
    if (!stripeCustomerId) {
      return null
    }

    const response = await createPortalSession({
      customerId: stripeCustomerId,
      returnUrl: `${window.location.origin}${AppRoutes.dashboard}`,
    })

    if ('error' in response) {
      return null
    }

    return response.data
  }

  /**
   * Generate a checkout topup session
   */
  const generateCheckoutTopupSession = async (
    stripeCustomerId: string,
  ): Promise<CheckoutSessionResponse | null> => {
    const topupProduct = topupProductData?.product

    if (!topupProduct || !stripeCustomerId) {
      return null
    }

    try {
      const response = await createCheckoutSession({
        mode: 'payment',
        lineItems: [
          {
            price: topupProduct.priceId,
            quantity: 1,
          },
        ],
        successUrl: `${window.location.origin}${AppRoutes.paymentSuccess}?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}${AppRoutes.dashboard}`,
        customerId: stripeCustomerId,
        allowPromotionCodes: true,
        metadata: {
          planType: topupProduct.type,
          credits: topupProduct.credits.toString(),
        },
      }).unwrap()

      return response
    } catch (error) {
      console.error('Error generating checkout session:', error)
      return null
    }
  }

  return {
    pricingProducts: pricingData?.products || [],
    isLoadingPricing,
    isPricingSuccess,
    topupProduct: topupProductData?.product,
    isLoadingTopupProductsData,
    handleSelectPlan,
    generatePortalSession,
    generateCheckoutTopupSession,
  }
}
